import { useState, useRef, useEffect, useCallback, type ReactNode, type Ref } from 'react';
import { Link } from 'react-router-dom';
import { api, type DocumentCard, type ListedSendCard, type MorningItemRef, type SendCard } from '../lib/api';
import ChatMarkdown from './ChatMarkdown';
import ChatDocumentCard from './ChatDocumentCard';
import { ChatBox, SendButton, CHAT_INPUT, ICON_BUTTON } from './chat/ChatBox';
import { PlusMenu, MenuSwitch, MENU_ROW } from './chat/PlusMenu';
import { ASSISTANT_BUBBLE, USER_BUBBLE } from './chat/bubbles';
import { ClipIcon, ClockIcon, MicIcon, PlusIcon, SpeakerIcon, WaveIcon } from './chat/icons';
import MorningSendCard from './morning/MorningSendCard';
import { supersedeEarlierCards } from '../lib/chatDocumentCards';
import { useReadAloud } from '../hooks/useReadAloud';
import { useServerVoice } from '../hooks/useServerVoice';
import { useDictation } from '../hooks/useDictation';
import { useVoiceChat, type VoiceChat } from '../hooks/useVoiceChat';
import { useKeyboardUp } from '../hooks/useKeyboardUp';
import { completeBlocks, speechForAnswer, toSpeech } from '../utils/speechText';
import { usePageSubject, type PageSubject } from '../contexts/PageSubjectContext';
import { usePageActionRunner, type PageActionFields, type PageActionName } from '../contexts/PageActionContext';
import {
  FINISHED_LETTER_OPEN, FINISHED_LETTER_REVISE, mayLeaveTheLetter, useAssistantRail, type LetterOnScreen,
} from '../contexts/AssistantRailContext';

/**
 * The assistant chat, in the rail.
 *
 * ⭐⭐ EXTRACTED ONCE FROM TWO COPIES. The corner bubble (527 lines) and the full-page
 * chat (600 lines) implemented this twice and shared no module - and they were not even
 * equivalent: the page streamed, the bubble did not, so the WORSE one was the one mounted on
 * every screen. This takes the streaming implementation and keeps the non-streaming call as its
 * fallback. Both files were deleted when the rail shipped; see docs/planning/ai-rail-design.md.
 */

interface Tool { name: string; label?: string }

/** What the backend is told about one uploaded file. `sendable` is the model-visible half:
 *  only these types can be handed to the model as content blocks, the rest are recorded on the
 *  message but not sent. Shape and semantics are the deleted components', unchanged. */
interface AttachmentMeta {
  file_name: string;
  file_size: number;
  mime_type: string;
  storage_path: string;
  sendable: boolean;
}

/**
 * The PDFs he has attached in this conversation, newest first and each once - the current message's
 * own files ahead of the transcript's. ⛔ Only PDFs: the OM read refuses anything else by its bytes.
 */
export const pdfAttachmentsOf = (
  transcript: { role: string; attachments?: AttachmentMeta[] }[],
  current: AttachmentMeta[],
): { storagePath: string; fileName: string }[] => {
  const earlier = [...transcript].reverse().flatMap((m) => (m.role === 'user' ? m.attachments ?? [] : []));
  const seen = new Set<string>();
  const out: { storagePath: string; fileName: string }[] = [];
  for (const a of [...current, ...earlier]) {
    if (a.mime_type !== 'application/pdf' || !a.storage_path || seen.has(a.storage_path)) continue;
    seen.add(a.storage_path);
    out.push({ storagePath: a.storage_path, fileName: a.file_name });
  }
  return out;
};

/** A file the operator picked but has not sent yet - nothing has left the browser at this point. */
interface PendingFile {
  file: File;
  uploading: boolean;
}

interface Msg {
  id?: string;
  role: 'user' | 'assistant';
  content: string;
  tools?: Tool[];
  streaming?: boolean;
  attachments?: AttachmentMeta[];
  /**
   * Why a dialog the answer asked for did NOT open.
   *
   * ⭐ Kept OUT of `content`, because content is what gets persisted as the chat message and read
   *   back later. "Nothing was opened - the record on screen changed" is true of this moment, not
   *   of the conversation; stored, it would reappear as a permanent claim about an answer that was
   *   fine. So it shows now and is not written down.
   */
  notes?: string[];
  /**
   * CHAT-DOCS Task 12: the paperwork this answer made (the stream's `document` events). ⭐ Like `notes`, shown and not
   *   persisted as message text - a reopened chat gets its cards from `listChatDocuments`, freshly signed.
   */
  documents?: DocumentCard[];
  /**
   * MORNING2 (Task 9): the email cards this answer showed (the stream's `send_card` events) - morning mode only. ⭐ Like
   *   `documents`, shown and not persisted as message text: a reloaded morning chat gets its cards from `send-cards`.
   */
  cards?: ListedSendCard[];
}

// ⛔ THE LIMITS ARE THE DELETED COMPONENTS' LIMITS, VERBATIM. Both the corner bubble and the
// full-page chat enforced exactly these, and the backend was built against them. Widening any of
// them here is a backend change wearing a frontend disguise.
const MAX_FILES = 3;
const MAX_SIZE = 25 * 1024 * 1024;

const ALLOWED_TYPES = new Set([
  'application/pdf', 'image/jpeg', 'image/png', 'image/webp',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  'application/vnd.ms-excel',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  'application/msword', 'text/csv', 'text/plain',
]);

/** The subset the model can actually be shown. A spreadsheet is accepted and filed, but it is
 *  NOT claimed to be readable by the model - that distinction is the whole point of the flag. */
const SENDABLE_TYPES = new Set(['application/pdf', 'image/jpeg', 'image/png', 'image/webp']);

function formatFileSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** How a human names each record kind. `property` and `master_property` are both "Property"
 *  on purpose - the operator does not think in terms of which table it came from. */
const KIND_LABEL: Record<PageSubject['type'], string> = {
  crm_deal: 'Deal',
  property: 'Property',
  master_property: 'Property',
};

/** Neutralizes the block's own separators - ":" (label/value) and "|" (fact/fact), plus any
 *  newline - inside field content, collapsing each to a space. Without this, a label containing
 *  ":" or a value containing " | " forges a fake boundary in the joined block, and that block is
 *  the entire corpus the model is told is the complete truth about the record. Collapsing (not
 *  dropping) keeps the field readable and never throws. */
function sanitizeFactField(v: string): string {
  return v.replace(/[:|\n\r]/g, ' ');
}

/**
 * The exact string sent as `page_context`, and therefore the exact block the model sees.
 *
 * ⛔ Exported so it is readable and testable rather than buried in a send handler. What is in
 * this string is the whole of what the model has been told about the record.
 */
export function renderFactBlock(s: PageSubject | null): string | undefined {
  if (!s) return undefined;
  const head = `${KIND_LABEL[s.type]}: ${s.label}`;
  if (!s.facts.length) return head;
  const line = s.facts
    .map((f) => `${sanitizeFactField(f.label)}: ${sanitizeFactField(f.value)}`)
    .join(' | ');
  return `${head}\n  ${line}`;
}

/** Plain, short marker for a HARD stream death (reader.read() itself rejecting - e.g. a backend
 *  redeploy killing the connection mid-answer, which this project's auto-deploy-on-push makes a
 *  real event, not a hypothetical). Same italic-underscore style as the in-loop graceful-error
 *  marker below, so the two read consistently to the operator. */
const CUT_OFF_MARKER = 'Connection lost - this response was cut off.';
/** ⛔ VOICE1, slice 3 - said ON the Voice chat control, on and off: in that mode his words go out without his reading them. */
const SENT_UNREAD = 'Sends what you say when you stop talking - you do not read it first.';
const READING_ALOUD = 'Reading the answer. Tap the microphone to cut in.';
/** VOICE-CUT1 (the 134th): in the back-and-forth mode the microphone works while an answer is still ARRIVING. */
const ARRIVING = 'The answer is arriving. Tap the microphone to cut in.';
const SENT_WHEN_LANDED = 'It is sent when this answer has finished arriving.';
/** MORNING2 (Task 9): a morning turn that could not reach the assistant at all - said, and his words handed back. */
const MORNING_UNREACHED = "Couldn't reach the assistant - try again.";
/** MORNING2 (Task 9): today's morning chat could not be read on load - ⛔ never shown as an empty chat. */
const CHAT_UNREAD = "Couldn't read this chat - try again.";
const CARDS_UNREAD = "Couldn't load this chat's email cards - try again.";

/** The words a refused stream came back with (`{ error }`), or the plain sentence when it carried none. */
const refusedWords = async (res: Response): Promise<string> => {
  try {
    const data = await res.json();
    if (data && typeof data.error === 'string' && data.error.trim()) return data.error;
  } catch { /* no words in it */ }
  return MORNING_UNREACHED;
};
/** A stream the server refused before it began (a status, not a stream): its words, for the morning chat to show. */
class TurnRefused extends Error {}

/** What a tap on the Morning page asks of the panel: one turn, with the item it started from. A new `seq` is a new tap. */
export interface MorningRequest { seq: number; text: string; item: MorningItemRef }

export default function AssistantPanel({
  onNavigateAway,
  openChatId,
  onChatOpened,
  hidden,
  mode = 'rail',
  lead,
  request,
  morningChatId = null,
  onSendingChange,
  onCardSent,
  inputRef,
}: {
  /**
   * MORNING2 (Task 9): `'morning'` = the chat on the Morning page (Joseph switched on). No New chat and no fact block;
   * every send carries `morning: true` (the server puts it in today's morning chat); email cards come from `send_card`
   * events and, on load, from `send-cards`; one page scroll - `lead` first, then the conversation, then the chat box
   * pinned at the bottom with the voice controls. ⛔ A stream that fails is said in words: NEVER the non-stream fallback
   * (that door is not a morning turn - the card tools refuse there, and after midnight it would land in yesterday's
   * chat). `'rail'` (the default) is the rail, unchanged.
   */
  mode?: 'rail' | 'morning';
  /** Morning mode: the first block of the message list - the Morning page's brief (ruling P8). */
  lead?: ReactNode;
  /** Morning mode: a tap on the page (Start) - a new `seq` sends `text` with `item` as `morning_item`, through `send`. */
  request?: MorningRequest;
  /** Morning mode: today's morning chat (`GET /morning` -> `chat.chat_id`), whose conversation and cards are read on load. */
  morningChatId?: string | null;
  /** Told whenever an answer starts or stops arriving - the page turns its Start buttons off meanwhile. */
  onSendingChange?: (sending: boolean) => void;
  /**
   * Morning mode: an email went from one of the chat's cards - the page reads its list again (the item leaves it). Told
   * of EVERY send, also one whose card the chat had since swapped for another draft (the card is then not stamped Sent).
   */
  onCardSent?: () => void;
  /** The chat box, for a page that scrolls to it and focuses it (the top bar's Assistant on /morning). */
  inputRef?: Ref<HTMLInputElement>;
  /** The rail keeps this panel MOUNTED behind the Thread tab (see AiRail). True while it is there, unseen: the
   *  back-and-forth mode must not go on listening, or sending, from a tab he is not looking at. */
  hidden?: boolean;
  /** Called when something inside the panel navigates away via a router Link (currently
   *  just History). AiRail supplies a handler that closes the rail when it is an overlay,
   *  so the destination is not left unreachable underneath it; docked mode passes nothing,
   *  since the rail sits beside the content and closing it would be an unrelated surprise.
   *  AssistantPanel does not know or care which mode it is in - it only calls what it was
   *  given, if anything. */
  onNavigateAway?: () => void;
  /**
   * UI3 - a general chat the rail's switcher asked to open, loaded IN PLACE.
   *
   * ⭐⭐ IN PLACE IS THE ENTIRE POINT. The only door to this history today is `History`, which
   * NAVIGATES to /ai - and on an overlay rail that also closes the rail, so reading an old chat
   * costs you the record you were standing on. A general chat is bound to no record, so nothing
   * stops it opening right here.
   */
  openChatId?: string | null;
  /** Cleared by the rail once the load has been taken, so re-picking the same chat re-loads it. */
  onChatOpened?: () => void;
}) {
  const subject = usePageSubject();
  const runPageAction = usePageActionRunner();
  /** MORNING2 (Task 9): the chat on the Morning page - see `mode`. */
  const morning = mode === 'morning';
  /** The letter on screen, read when an answer's actions land (an answer can take ten seconds). `null` outside Layout. */
  const rail = useAssistantRail();
  const railRef = useRef(rail);
  railRef.current = rail;
  const [messages, setMessages] = useState<Msg[]>([]);
  /**
   * VOICE1, size A (the 130th): the answers read aloud by the device's own voice. His words: "can we add a voice to the
   * AI chat ... if it could talk back to me". ⛔ What is said is what is WRITTEN in this transcript - `speechText.ts`
   * turns an answer and its amber notes into sentences; nothing else is ever handed to the voice.
   */
  /** VOICE1, slice 3: the back-and-forth mode, reached from the two hooks' callbacks, which exist before it does. */
  const voiceChatRef = useRef<VoiceChat | null>(null);
  // VOICE1-B (slice 0): the server's voice, when it has one - one more entry in the Voice menu, and clips instead of
  // the phone's own voice once he picks it. With none set up (asked once, quietly) read aloud is what it was.
  const serverVoice = useServerVoice();
  const readAloud = useReadAloud({ onIdle: () => voiceChatRef.current?.voiceFinished(), server: serverVoice });
  /** For the effects below, which must stop the voice without re-running when it changes. */
  const readAloudRef = useRef(readAloud);
  readAloudRef.current = readAloud;
  const [draft, setDraft] = useState('');
  const [sending, setSending] = useState(false);
  /** CHAT-DUET: the chat box's + menu (Attach files, Read aloud, Voice chat, History) is open. */
  const [menuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  /**
   * VOICE-CUT1 (the 134th) - CUT IN AT ANY MOMENT. His phone, on step two: "theres no way to interrupt it is there?" -
   * then "id like to be able to cut in as well." In the back-and-forth mode a tap on the microphone works while an
   * answer is still ARRIVING: the voice stops, the rest of THAT answer is not read aloud (it still lands on the
   * screen), and the phone listens.
   * ⛔ WHAT HE SAYS MEANWHILE IS NEVER THROWN AWAY, AND THE TRANSCRIPT STAYS IN ORDER: his words are `held` - shown,
   *    waiting - and are sent the moment that answer has landed. If the conversation ended meanwhile, or he is not
   *    looking when it lands, they go to the box and wait for Send.
   */
  /** He tapped the microphone while this turn's answer was still arriving. Cleared as the next turn begins. */
  const cutIn = useRef(false);
  const [held, setHeld] = useState<string | null>(null);
  /**
   * VOICE1, size A, slice 2: he talks, the words land in the box. ⛔ THEY WAIT FOR HIS TAP ON SEND - a misheard owner's
   * name or price must not go out by itself. What the phone finished hearing is added AFTER what he had typed.
   */
  const sendRef = useRef<(spoken?: string, item?: MorningItemRef) => Promise<void>>(async () => undefined);
  /** His spoken words, put in the box AFTER what he had typed - where they wait for his tap on Send. */
  const intoTheBox = (words: string): void => setDraft((typed) => (typed.trim() ? `${typed.trim()} ${words}` : words));
  const dictation = useDictation({
    onFinal: (heard) => {
      // ⛔ VOICE1, slice 3 - ONLY in the mode he turned on knowingly: what he said is SENT, as he stops talking.
      if (voiceChatRef.current?.on) {
        // ⛔ VOICE-CUT1: he cut in, and the answer he cut into is STILL ARRIVING. A second message beside it would
        //   put the transcript out of order - so his words are HELD, on the screen, and sent the moment it has landed.
        if (sending) { setHeld((had) => (had ? `${had} ${heard}` : heard)); return; }
        void sendRef.current(heard);
        return;
      }
      intoTheBox(heard);
    },
    onNothing: (why) => voiceChatRef.current?.heardNothing(why),
  });
  const dictationRef = useRef(dictation);
  dictationRef.current = dictation;
  /**
   * VOICE1, size A, slice 3 (the 132nd) - THE BACK-AND-FORTH MODE. His words: "I had to push send still ... it doesn't
   * work like, you know, OpenAI or, or Claude, the voice chat." One tap, he talks, it sends when he stops, reads the
   * answer, listens again - or, on a phone that will not listen by itself, "Tap the microphone to answer." a turn.
   * `useVoiceChat` decides; this panel tells it when a turn begins and ends, and does the sending.
   */
  const voiceChat = useVoiceChat({ readAloud, dictation });
  voiceChatRef.current = voiceChat;
  /**
   * MORNING2 - his iPhone findings (2026-10-28): the Morning chat box knows when he is TYPING in it (the phone's keyboard
   * up for its text box - `useKeyboardUp`). Then the box is the input row alone - voice controls are not changed mid-
   * sentence; it was 161 px of a 385 px view - and a white strip lies under it, where the keyboard's see-through ✓ bar
   * showed the list. ⛔ Never in Voice chat or while listening: those rows are what he is using.
   */
  const morningPanelRef = useRef<HTMLDivElement>(null);
  const morningBarRef = useRef<HTMLDivElement>(null);
  const keyboard = useKeyboardUp({ enabled: morning, bar: morningBarRef, panel: morningPanelRef });
  const typingInMorningBox = keyboard.up && !voiceChat.on && dictation.state === 'idle';
  /** `hidden` as it is NOW - an answer can take ten seconds to land, and he may have changed tabs meanwhile. */
  const hiddenRef = useRef(hidden);
  hiddenRef.current = hidden;
  /** An answer's action really OPENED something on the page - a letter is read and filled by hand, not talked over. */
  const openedByAnswer = useRef(false);
  const [chatId, setChatId] = useState<string | null>(null);
  const [pendingFiles, setPendingFiles] = useState<PendingFile[]>([]);
  // One line, one place to look: why a picked file was dropped, or why a send did not happen.
  const [attachError, setAttachError] = useState<string | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  // Bumped by newChat(). A send() in flight captures the value at its start; if a late stream
  // event arrives after newChat() bumped it, the mismatch tells that event it belongs to an
  // abandoned chat, so it skips writing state instead of re-pointing the fresh chat at the old
  // one (or dropping a message that belongs to the new chat).
  const generationRef = useRef(0);
  /**
   * ⛔⛔ FOUND BY LOOKING, and it is the reason this flag exists rather than a nicety.
   * Opening an earlier chat in the rail shows a conversation that may be about a DIFFERENT
   * record - measured live: an old chat about a 12,036 SF building, read while standing on a
   * 5,000 SF one. A follow-up typed there continues THAT chat (`chat_id` is sent) but carries
   * THIS page's fact block, so the model would receive one record's conversation and another
   * record's facts. That is the defect class this build is made of, and the house rule is
   * magic where certain, **a visible question where not, never a silent guess.** So the panel
   * SAYS it is showing an earlier chat, and offers the way out.
   */
  const [viewingEarlierChat, setViewingEarlierChat] = useState(false);

  /**
   * MORNING2 (Task 9): the chat whose WHOLE conversation is on the screen - read on load, or begun here with nothing
   *   before it. A page re-read naming that chat again reads nothing; naming another (a new day's) reads that one.
   */
  const loadedFor = useRef<string | null>(null);
  const morningChatIdRef = useRef(morningChatId);
  morningChatIdRef.current = morningChatId;
  /** Today's chat or its cards could not be read on load - said with Try again, ⛔ never an empty chat. */
  const [loadProblem, setLoadProblem] = useState<string | null>(null);
  const [loadTries, setLoadTries] = useState(0);
  const sendingRef = useRef(false);
  sendingRef.current = sending;
  /** MORNING2: how many turns have begun here - a load that read the chat before one began is older than the screen. */
  const turnsBegun = useRef(0);
  const onCardSentRef = useRef(onCardSent);
  onCardSentRef.current = onCardSent;

  /**
   * The subject as it is RIGHT NOW, not as it was when a message was sent.
   *
   * ⭐⭐⭐ THE WHOLE POINT IS THAT IT IS NOT THE CLOSURE. `send()` is async and captures `subject`
   *    at the moment it runs; comparing that captured value against itself would pass forever and
   *    prove nothing - the exact shape of the S2 defect this repo records ("a test that compares a
   *    thing to itself"). An answer can take ten seconds, and an operator can walk to another
   *    property in that time. This ref is what makes the comparison below a real one.
   */
  const subjectRef = useRef<PageSubject | null>(subject);
  subjectRef.current = subject;
  /** The transcript as of the last render - read at SEND time for the PDFs he has attached. */
  const messagesRef = useRef<Msg[]>([]);
  messagesRef.current = messages;

  /**
   * Open a dialog the answer asked for, on the page the question was asked about.
   *
   * ⛔⛔ IT REFUSES WHEN THE RECORD ON SCREEN CHANGED. Ask "make a brochure for this property",
   *    navigate to a different property while the model is thinking, and the action must NOT open
   *    the editor loaded with the property you are now looking at. That is `fileToTarget`'s
   *    failure - a plausible pairing nobody checked - and the answer here is the same as the one
   *    the human-at-approve gives there: refuse, and SAY why. Going quiet would be worse than
   *    opening the wrong one, because nothing would tell him which record it used.
   */
  /**
   * ⚠️⚠️ A LOOKUP, NOT A TERNARY - and the third action is why. This was
   * `name === 'open_loi_dialog' ? 'the LOI dialog' : 'the brochure editor'`, which was correct for
   * exactly as long as there were two actions: the moment LP1.4 added a third, a lease-proposal
   * request on a page without that dialog would have been refused with the words **"this page does
   * not offer the brochure editor"** - a true refusal describing the wrong thing, which is the same
   * family as the 85th's "expiration left blank" over a field that had defaulted.
   */
  /**
   * ⛔⛔ WHICH BUILDING THE LETTER IS ABOUT. Honoured ONLY when there is no record on screen;
   *    see `dispatchActions`. ⚠️ Keep in step with the property fields `appAssistantActions`
   *    emits - a new one added there and classified in neither list would reach a dialog
   *    unfiltered (`appAssistantActions.guard.test.ts` reads both lists and fails on it).
   */
  const PROPERTY_IDENTITY_FIELDS = new Set([
    'address', 'city', 'state', 'zip', 'county',
    // The 98th - the listing proposal's spelling of the address.
    'addressLine',
  ]);
  /**
   * ⭐ FACTS ABOUT THE BUILDING, not which one it is. Stripped on a record page like the identity above, EXCEPT by the
   *   documents in `FACTS_REACH_ON_A_RECORD`. ⭐ The 114th - his answer 2026-10-01: a building fact he states in the
   *   chat reaches the LETTER on a record page, and its correction note says where it differs from the record.
   */
  const PROPERTY_FACT_FIELDS = new Set([
    'buildingSf', 'acres', 'description',
    // The 98th - the listing proposal describes its property with these two.
    'propertyType', 'zoning',
    // The 101st - his September RE line describes the BUILDING.
    'reLineTenant', 'lotSf', 'reLineLocation',
  ]);
  /** ⛔ The LOI only: his answer was about the letter, the one dialog whose correction note speaks for a stated fact. */
  const FACTS_REACH_ON_A_RECORD: ReadonlySet<PageActionName> = new Set(['open_loi_dialog']);

  const DIALOG_NAMES: Record<PageActionName, string> = {
    open_brochure_editor: 'the brochure editor',
    open_loi_dialog: 'the LOI dialog',
    open_lease_proposal_dialog: 'the lease proposal dialog',
    open_listing_proposal_dialog: 'the executive summary dialog',
    revise_open_loi: 'the open LOI',
  };

  const dispatchActions = useCallback(
    (
      actions: { name: PageActionName; fields: PageActionFields }[],
      askedAboutId: string | null,
      conversationPdfs: { storagePath: string; fileName: string }[] = [],
    ): string[] => {
      const notes: string[] = [];
      for (const action of actions) {
        const now = subjectRef.current;

        /**
         * ⭐⭐⭐ THE 97TH: NO RECORD IS A CASE, NOT AN ERROR. He asks for an LOI while pricing an
         *    OM that is not in the app yet - `buildLoiOnly` needs no record and writes nothing,
         *    so the rail hosts the dialog itself.
         *
         * ⛔⛔ AND IT IS DECIDED BY WHAT WAS ON SCREEN WHEN HE ASKED, never by what is there now.
         *    Navigating onto a property while the answer streams must NOT promote a recordless
         *    request into a letter bound to that property - he never named it. That is the
         *    `fileToTarget` failure from the other direction, so `recordless` is latched from
         *    `askedAboutId` and the two paths never cross.
         */
        const recordless = askedAboutId === null;

        /**
         * 🚨🚨🚨 THE PAGE OWNS WHICH BUILDING IT IS - ALWAYS. `appAssistantActions` may now carry
         *    property facts, because a recordless letter has to be able to name its own subject.
         *    With a record on screen those same fields are an IDENTIFIER, and `LoiPrefill` has an
         *    `address`, so an unfiltered spread would prefill the dialog with a DIFFERENT
         *    building from the one he is looking at. That is `fileToTarget` exactly, and the
         *    forbidden-key guard in `appAssistantActions.guard.test.ts` caught it being built.
         *
         * ⭐ The TERMS still pass either way - a price or an EMD was always his to state. It is
         *   only the identity of the property that the page owns and the model never does.
         * ⛔⛔ THE 113TH: A RECORD THAT OWNS NO BUILDING - a deal with no property attached - has none to protect, and
         *    dropping his facts there left the letter with no building at all (live, his Test deal: "123 Test Street"
         *    stated, passed by the model, and gone). Only a record that says it owns none (`ownsBuilding: false`) keeps
         *    them; a record that says nothing is taken to own its building, so the strip stays the default.
         */
        const pageOwnsBuilding = !recordless && now?.ownsBuilding !== false;
        const factsReach = FACTS_REACH_ON_A_RECORD.has(action.name);
        const fields = !pageOwnsBuilding
          ? action.fields
          : Object.fromEntries(
              Object.entries(action.fields)
                .filter(([k]) => !PROPERTY_IDENTITY_FIELDS.has(k) && (factsReach || !PROPERTY_FACT_FIELDS.has(k)))
            );

        if (!recordless && (!now || now.id !== askedAboutId)) {
          notes.push(
            'Nothing was opened - the record on screen changed while this answer was being ' +
            'written, so it would have been the wrong one. Ask again from the record you want.'
          );
          continue;
        }
        /**
         * ⭐ The 112th's review (I1): a FINISHED letter on screen - its change door is closed, and a page's open request
         *   leaves its finished screen as it is - so neither action can do what he asked, and "No LOI is open" beside it
         *   was false. Said in words that are true where he acts.
         */
        if ((action.name === 'revise_open_loi' || action.name === 'open_loi_dialog') && railRef.current?.letterOnScreen === 'finished') {
          notes.push(action.name === 'revise_open_loi' ? FINISHED_LETTER_REVISE : FINISHED_LETTER_OPEN);
          continue;
        }
        /**
         * ⭐ THE 100TH - "an OM I attach". The PDFs he attached in this conversation go to the LOI
         *   dialog, which offers each one BY NAME for the OM read. ⛔ Nothing is read until he
         *   presses it, and the read itself refuses price, EMD, expiration, seller and cap rate.
         */
        const withPdfs: PageActionFields =
          action.name === 'open_loi_dialog' && conversationPdfs.length
            ? { ...fields, omAttachments: conversationPdfs }
            : fields;

        const opened = runPageAction(action.name, withPdfs, recordless);
        if (opened) openedByAnswer.current = true;
        if (!opened) {
          /**
           * ⚠️ TWO DIFFERENT FAILURES, TWO DIFFERENT SENTENCES. Telling him to "open the
           * property and ask again" when he deliberately asked WITHOUT one sends him to do the
           * very thing this session exists to stop being necessary - and the old code said the
           * record had CHANGED, on a page that never had one.
           * ⭐ A THIRD: a change to the open LOI finds nothing when no LOI is open - on any page, with or without a
           *   record - because only an open letter answers it. So it says exactly that, and how to get one.
           */
          notes.push(
            action.name === 'revise_open_loi'
              ? 'No LOI is open - ask me to make one, and say what to change.'
              : recordless
                ? `Nothing was opened - ${DIALOG_NAMES[action.name] ?? 'that'} cannot be opened `
                  + 'from here without a record. Say which property you mean, or open it first.'
                : `Nothing was opened - this page does not offer ${DIALOG_NAMES[action.name] ?? 'that'}. `
                  + 'Open the property or deal and ask again.'
          );
        }
      }
      return notes;
    },
    [runPageAction]
  );

  useEffect(() => {
    // MORNING2: on the Morning page the whole page scrolls - it follows an answer while it arrives, never a load or a
    // card's own change (he may be reading the brief, or editing a card above).
    if (morning && !sendingRef.current) return;
    bottomRef.current?.scrollIntoView({ block: 'end' });
  }, [messages, morning]);

  /** MORNING2: an email card where it already is (a revise re-emits the same id), else under the newest answer. */
  const putCard = useCallback((card: ListedSendCard) => {
    setMessages((prev) => {
      const at = prev.findIndex((m) => m.cards?.some((c) => c.id === card.id));
      if (at >= 0) {
        return prev.map((m, i) => (i === at ? { ...m, cards: (m.cards ?? []).map((c) => (c.id === card.id ? card : c)) } : m));
      }
      if (!prev.length) return prev;
      const last = prev[prev.length - 1];
      return [...prev.slice(0, -1), { ...last, cards: [...(last.cards ?? []), card] }];
    });
  }, []);
  /** A card's own change (a save, a send, Don't send). */
  const cardChanged = useCallback((card: SendCard) => { putCard(card); }, [putCard]);
  /** An email went from a card: the page reads its list again. */
  const cardSent = useCallback(() => { onCardSentRef.current?.(); }, []);

  const patchLast = useCallback((fn: (m: Msg) => Msg) => {
    setMessages((prev) => (prev.length ? [...prev.slice(0, -1), fn(prev[prev.length - 1])] : prev));
  }, []);

  /**
   * The picker. Filters by type and by size and caps the total at MAX_FILES, exactly as the two
   * deleted components did.
   *
   * ⭐ The one thing added: a dropped file SAYS SO. The originals skipped silently, so a 30 MB OM
   * simply never appeared and the operator had no way to tell a rejection from a misclick. The
   * limits themselves are untouched - only the silence is.
   */
  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;
    const currentCount = pendingFiles.length;
    const accepted: PendingFile[] = [];
    const rejected: string[] = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (!ALLOWED_TYPES.has(file.type)) { rejected.push(`${file.name} is not a supported file type`); continue; }
      if (file.size > MAX_SIZE) { rejected.push(`${file.name} is larger than 25 MB`); continue; }
      if (currentCount + accepted.length >= MAX_FILES) { rejected.push(`${file.name} exceeds the limit of ${MAX_FILES} files`); continue; }
      accepted.push({ file, uploading: false });
    }
    setPendingFiles((prev) => [...prev, ...accepted]);
    setAttachError(rejected.length ? `${rejected.join('. ')}.` : null);
    // Let the same file be picked again after a removal - without this the input keeps its old
    // value and re-selecting it fires no change event.
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const removePendingFile = (index: number) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
    setAttachError(null);
  };

  /**
   * Signed URL per file, then a direct PUT to storage, then the metadata the message carries.
   *
   * 🚨 THROWS on any file that does not land. The deleted chat swallowed a failed PUT and simply
   * omitted that attachment, so the message went out reading as though the file were attached
   * while the model saw nothing - the worst of both. The caller aborts the send instead.
   *
   * `mySeq` gates the only state write in here: a "New chat" during an upload must not repaint
   * spinners onto the fresh chat's composer.
   */
  const uploadPendingFiles = async (mySeq: number): Promise<AttachmentMeta[]> => {
    const files = pendingFiles;
    const res = await api.assistantUploadUrl({
      files: files.map((pf) => ({
        file_name: pf.file.name,
        file_size: pf.file.size,
        mime_type: pf.file.type,
      })),
    });
    const uploads = res?.uploads ?? [];
    const attachments: AttachmentMeta[] = [];
    for (let i = 0; i < files.length; i++) {
      const pf = files[i];
      const upload = uploads[i];
      if (!upload?.signedUrl) throw new Error(`${pf.file.name} could not be prepared for upload. Nothing was sent.`);
      if (mySeq === generationRef.current) {
        setPendingFiles((prev) => prev.map((f, idx) => (idx === i ? { ...f, uploading: true } : f)));
      }
      const put = await fetch(upload.signedUrl, {
        method: 'PUT',
        headers: { 'Content-Type': pf.file.type },
        body: pf.file,
      });
      if (!put.ok) throw new Error(`${pf.file.name} could not be uploaded. Nothing was sent.`);
      attachments.push({
        file_name: pf.file.name,
        file_size: pf.file.size,
        mime_type: pf.file.type,
        storage_path: upload.storagePath,
        sendable: SENDABLE_TYPES.has(pf.file.type),
      });
    }
    return attachments;
  };

  const send = async (spoken?: string, item?: MorningItemRef) => {
    // VOICE1, slice 3: a turn he SPOKE in the back-and-forth mode arrives here as `spoken`; the box is not read.
    // MORNING2: a tap on the Morning page arrives the same way, with the `item` it started from.
    const text = (spoken ?? draft).trim();
    if (!text || sending) return;
    voiceChatRef.current?.turnBegan();
    // ⛔ VOICE-CUT1: a turn that follows one he CUT INTO follows an answer he did not hear out, whose own pause was
    //   given up. If that answer opened something to be worked by hand, the answer to THIS turn waits for his tap
    //   instead - once: the mark is cleared here, so the turn after this one starts clean.
    if (!cutIn.current) openedByAnswer.current = false;
    cutIn.current = false;
    /**
     * ⛔ VOICE1 - BEFORE ANYTHING IS AWAITED. This press is the tap that lets the device speak the answer that arrives
     *   seconds later: measured on iOS 26, speech asked for outside a tap is silently dropped until something was
     *   spoken inside one. So the answer still being read is cut off (he has moved on) and one silent word is said NOW.
     */
    readAloud.stop();
    readAloud.prime();
    // A message sent while the microphone is open is the one he TYPED: the half-heard words are thrown away.
    dictation.cancel();
    /** How many of this answer's sentences have been handed to the voice - a paragraph is said as it lands, once. */
    let saidSoFar = 0;
    const sayUpTo = (sentences: string[]): void => {
      if (sentences.length <= saidSoFar) return;
      // ⛔ VOICE1, slice 3: in the back-and-forth mode nothing is read to a chat he has put behind the other tab -
      //   he left a conversation. It is on the screen when he comes back.
      // ⛔ VOICE-CUT1: nor is the rest of an answer he CUT INTO. It goes on landing on the screen, unread.
      if (!cutIn.current && !(voiceChatRef.current?.on && hiddenRef.current)) readAloud.say(sentences.slice(saidSoFar));
      saidSoFar = sentences.length;
    };
    const mySeq = generationRef.current;
    turnsBegun.current += 1;
    // What he had typed is still his to send: a spoken turn leaves the box as it was.
    if (spoken === undefined) setDraft('');
    setSending(true);
    setAttachError(null);

    // ⭐ Upload BEFORE the user message is shown, as the deleted full-page chat did: the message
    // is created already carrying its attachments, so there is no window in which the transcript
    // claims a file that is not in storage yet.
    // ⛔ The no-files path stays fully synchronous up to setMessages - an await here would move
    // the user's own message a tick later than it has ever appeared, for no gain.
    let attachments: AttachmentMeta[] = [];
    if (pendingFiles.length > 0) {
      try {
        attachments = await uploadPendingFiles(mySeq);
      } catch (err: any) {
        // ⛔ Do NOT send anyway. The question is ABOUT the file; asking it without the file gets a
        // confident answer about nothing. Put the draft back, keep the chips, and say what broke.
        if (mySeq === generationRef.current) {
          setAttachError(err?.message || 'That attachment could not be uploaded. Nothing was sent.');
          // What he TYPED goes back in the box it was taken from. What he SPOKE goes in after whatever he was typing -
          // the box was never emptied for it, and a note he was writing is not wiped by words that could not be sent.
          if (spoken === undefined) setDraft(text);
          else intoTheBox(text);
          setPendingFiles((prev) => prev.map((f) => ({ ...f, uploading: false })));
        }
        setSending(false);
        // VOICE1, slice 3: nothing was sent, so there is no answer to wait for - it says so, and waits for his tap.
        voiceChatRef.current?.turnEnded(false);
        return;
      }
      if (mySeq !== generationRef.current) { setSending(false); return; }
      setPendingFiles([]);
    }

    /** His turn as shown - kept by identity, so a morning turn that never got an answer can take it back off. */
    const mine: Msg = {
      role: 'user',
      content: text,
      attachments: attachments.length ? attachments : undefined,
    };
    setMessages((prev) => [...prev, mine]);

    const body: {
      message: string; chat_id?: string | null; page_context?: string; attachments?: AttachmentMeta[];
      letter_on_screen?: LetterOnScreen; subject?: { type: PageSubject['type']; id: string };
      morning?: true; morning_item?: MorningItemRef;
    } = {
      message: text,
      chat_id: chatId,
    };
    /** MORNING2: nothing was on the screen to read before this turn - the conversation it begins is all there is. */
    const nothingBefore = morning && !morningChatIdRef.current;
    if (morning) {
      // ⛔ MORNING2: a morning turn lands in today's morning chat on the server (whatever `chat_id` says), carries NO fact
      //   block and no record, and carries the item his tap started it from - never one the model names.
      body.morning = true;
      if (item) body.morning_item = item;
    } else {
      const block = renderFactBlock(subject);
      if (block) body.page_context = block;
      /**
       * ⭐ DOC-READ (the 157th): the record this page shows travels with the message, for the SERVER's document reader -
       *   "look at the title profile again" reads the documents of THIS property, never one the model named. ⛔ It is not
       *   in the fact block: the model still reads no id.
       */
      if (subject) body.subject = { type: subject.type, id: subject.id };
    }
    if (attachments.length) body.attachments = attachments;
    /**
     * ⛔ The 113th's O3: the letter on his screen travels with the message, so the chat's letter tools answer what is TRUE
     *   of it - beside a finished letter the model wrote "Done - that's drafted", knowing nothing. No rail, no report.
     */
    if (railRef.current) body.letter_on_screen = railRef.current.letterOnScreen;

    let streamed = false;
    let accumulated = '';
    // Captured HERE, at send time, so the dispatcher can tell whether he is still standing on the
    // record he asked about by the time the answer lands.
    const askedAboutId = subject?.id ?? null;
    /**
     * ⭐ Latched at SEND time, like `askedAboutId`: this message's own attachments FIRST (the
     *   transcript has not re-rendered with them yet), then the earlier ones, newest first.
     */
    const conversationPdfs = pdfAttachmentsOf(messagesRef.current, attachments);
    const pendingActions: { name: PageActionName; fields: PageActionFields }[] = [];
    /** MORNING2: an email card arrived - the turn reached the server's tools, so it is kept even with no text. */
    let sawCard = false;
    try {
      const res = await api.assistantChatStream(body);
      if (!res.ok || !res.body) {
        // ⛔ MORNING2: a refused morning turn says the server's own words ("Couldn't read this chat - try again.").
        if (morning) throw new TurnRefused(await refusedWords(res));
        throw new Error(`stream unavailable (${res.status})`);
      }

      setMessages((prev) => [...prev, { role: 'assistant', content: '', tools: [], streaming: true }]);
      streamed = true;

      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let buf = '';
      let sawError: string | null = null;
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += decoder.decode(value, { stream: true });
        let nl: number;
        while ((nl = buf.indexOf('\n')) >= 0) {
          const line = buf.slice(0, nl).trim();
          buf = buf.slice(nl + 1);
          if (!line) continue;
          let ev: any;
          try { ev = JSON.parse(line); } catch { continue; }
          // A newChat() since this send() started means this event belongs to an abandoned
          // stream - do not let it touch chatId or messages.
          if (mySeq !== generationRef.current) continue;
          if (ev.type === 'chat') {
            setChatId(ev.chat_id);
            if (nothingBefore && loadedFor.current === null) loadedFor.current = ev.chat_id;
          }
          else if (ev.type === 'text') {
            accumulated += ev.delta;
            patchLast((m) => ({ ...m, content: m.content + ev.delta }));
            // VOICE1: a paragraph is said as soon as it has finished landing; the one still arriving is held back,
            // so "$3,5" is never said for "$3,550,000" and half a table is never read as prose.
            sayUpTo(toSpeech(completeBlocks(accumulated).done));
          }
          else if (ev.type === 'tool') patchLast((m) => ({ ...m, tools: [...(m.tools ?? []), { name: ev.name, label: ev.label }] }));
          // ⚠️ COLLECTED, NOT RUN HERE. Opening a dialog over a half-written answer would cover
          // the sentence explaining it; these run once the text has finished landing.
          else if (ev.type === 'action') pendingActions.push(ev.action);
          // CHAT-DOCS: a made document lands on the answer that made it, as it is made.
          // ⭐ And every earlier card of the same document is superseded on screen at once (anti-goal 8).
          else if (ev.type === 'document' && ev.card) {
            const card = ev.card;
            setMessages((prev) => {
              const marked = supersedeEarlierCards(prev, card);
              if (!marked.length) return marked;
              const last = marked[marked.length - 1];
              return [...marked.slice(0, -1), { ...last, documents: [...(last.documents ?? []), card] }];
            });
          }
          // MORNING2: an email card the answer showed - where it already is when the chat revised it, else under this answer.
          else if (ev.type === 'send_card' && ev.card && morning) {
            sawCard = true;
            putCard(ev.card);
          }
          else if (ev.type === 'done') patchLast((m) => ({ ...m, id: ev.message_id, streaming: false }));
          else if (ev.type === 'error') sawError = ev.message || 'The assistant hit an error.';
        }
      }
      // 🚨 A stream that dies mid-answer KEEPS what it already said. The operator may have been
      // reading it; deleting it is worse than saying it was cut off.
      if (mySeq === generationRef.current) {
        // The answer has finished landing, so now open what it asked for. A refusal comes back as
        // a note and is shown under the answer - never swallowed.
        const notes = dispatchActions(pendingActions, askedAboutId, conversationPdfs);
        patchLast((m) => ({
          ...m,
          streaming: false,
          content: sawError ? (m.content ? `${m.content}\n\n_${sawError}_` : `Sorry, I encountered an error: ${sawError}`) : m.content,
          notes: notes.length ? notes : undefined,
        }));
        // VOICE1: the rest of the answer as it now stands on the screen - and ⛔ its amber notes after it. A note
        // says a thing the answer claimed did NOT happen; an answer heard without it is worse than silence.
        sayUpTo(speechForAnswer({
          content: sawError
            ? (accumulated ? `${accumulated}\n\n_${sawError}_` : `Sorry, I encountered an error: ${sawError}`)
            : accumulated,
          notes,
        }));
      }
    } catch (err) {
      // This is the OTHER death: reader.read() itself rejecting (a hard connection loss), not
      // the graceful {type:'error'} event handled above. Same rule applies - keep partial text
      // rather than delete it - but only once there IS partial text to keep. An empty placeholder
      // has nothing worth keeping, so that case still falls back exactly as before.
      if (streamed && (accumulated || sawCard)) {
        if (mySeq === generationRef.current) {
          patchLast((m) => ({ ...m, streaming: false, content: `${m.content}\n\n_${CUT_OFF_MARKER}_` }));
          // VOICE1: what had arrived, and that it was cut off - he hears what the screen says.
          sayUpTo(toSpeech(`${accumulated}\n\n_${CUT_OFF_MARKER}_`));
        }
        // Deliberately no fallback call here: re-answering would replace what the operator may
        // already be reading with a possibly different answer.
      } else if (morning) {
        /**
         * ⛔⛔ MORNING2: NEVER THE FALLBACK. `POST /assistant/chat` is not a morning turn - the card tools refuse there,
         *   and after midnight it would land in yesterday's chat. Nothing was answered, so his turn comes off the screen,
         *   the words say why, and what he typed or said goes back in the box (a tap is tapped again).
         */
        if (mySeq === generationRef.current) {
          const words = err instanceof TurnRefused ? err.message : MORNING_UNREACHED;
          setMessages((prev) => {
            const out = prev.filter((m) => m !== mine);
            const last = out[out.length - 1];
            return streamed && last?.role === 'assistant' && !last.content ? out.slice(0, -1) : out;
          });
          setAttachError(words);
          if (item === undefined) {
            if (spoken === undefined) setDraft(text);
            else intoTheBox(text);
          }
          sayUpTo([words]);
        }
      } else {
        if (streamed && mySeq === generationRef.current) setMessages((prev) => prev.slice(0, -1));
        try {
          const res = await api.assistantChat(body);
          if (mySeq === generationRef.current) {
            setChatId(res.chat_id);
            // ⛔ THE FALLBACK DISPATCHES TOO. The stream died before any text, so no `action`
            // event was ever seen; without this the model's "the editor is open" would be true of
            // a screen where nothing opened. `actions` on the JSON response exists for this line.
            const notes = dispatchActions(res.actions ?? [], askedAboutId, conversationPdfs);
            setMessages((prev) => [...(res.documents ?? []).reduce(supersedeEarlierCards, prev), {
              id: res.message_id, role: 'assistant', content: res.message,
              notes: notes.length ? notes : undefined,
              // CHAT-DOCS: the fallback carries the same cards the stream would have.
              documents: res.documents?.length ? res.documents : undefined,
            }]);
            sayUpTo(speechForAnswer({ content: res.message, notes }));
          }
        } catch {
          if (mySeq === generationRef.current) {
            setMessages((prev) => [...prev, { role: 'assistant', content: 'Sorry, I could not reach the assistant.' }]);
            sayUpTo(['Sorry, I could not reach the assistant.']);
          }
        }
      }
    } finally {
      setSending(false);
      // VOICE1, slice 3: the answer has landed and been handed to the voice - the mode listens again once it has been
      // read, unless something was opened for him to fill by hand. (A chat he has since abandoned ended the mode.)
      // ⛔ VOICE-CUT1: a turn he cut into owes no listening. His own tap took the microphone: he is still talking,
      //   or his words are waiting to be sent, or he said nothing - and silence is what stops it.
      if (!cutIn.current) voiceChatRef.current?.turnEnded(!openedByAnswer.current);
    }
  };
  sendRef.current = send;

  /**
   * VOICE-CUT1: what becomes of the words he cut in with. While the mode is on and the answer he cut into is still
   * arriving, they WAIT (and are shown). The moment it has landed they are sent - after it, never beside it.
   * ⛔ Not sent when the conversation has ended (Voice chat off, Read aloud off, an earlier chat opened), nor from a
   *    chat he is not looking at when it lands: then they go to the box, where he can read them, and wait for Send.
   */
  const modeOn = voiceChat.on;
  useEffect(() => {
    if (held === null || (modeOn && sending)) return;
    setHeld(null);
    const lookingAtIt = !hiddenRef.current && document.visibilityState !== 'hidden';
    if (modeOn && lookingAtIt) { void sendRef.current(held); return; }
    intoTheBox(held);
  }, [held, sending, modeOn]); // eslint-disable-line react-hooks/exhaustive-deps

  // He can no longer see the chat (the rail's other tab is in front): the mode stops listening and speaking.
  useEffect(() => {
    if (hidden) voiceChatRef.current?.leave();
  }, [hidden]);

  /**
   * MORNING2 (Task 9): a tap on the Morning page (Start) - ONE turn through this panel's own send, exactly as if he had
   *   typed it, with the item it started from. A request already taken (the same `seq` on a re-render) is not sent again;
   *   one tapped while an answer arrives is dropped by `send` itself (the page's Start buttons are off meanwhile).
   */
  const lastRequest = useRef(request?.seq);
  useEffect(() => {
    if (!request || request.seq === lastRequest.current) return;
    lastRequest.current = request.seq;
    void sendRef.current(request.text, request.item);
  }, [request]);

  const onSendingChangeRef = useRef(onSendingChange);
  onSendingChangeRef.current = onSendingChange;
  useEffect(() => { onSendingChangeRef.current?.(sending); }, [sending]);

  /**
   * MORNING2 (Task 9): today's morning chat on load - its conversation, then its email cards after it (which answer made
   *   which card is not stored, and a card pinned to the wrong answer would be a claim). Each open card is re-read from
   *   Gmail by the server. ⛔ A read that fails is SAID, with Try again - never an empty chat; cards that cannot be read
   *   leave the conversation standing and say so. ⛔ A load is never shown over a turn that began after it started
   *   reading (the turn is newer than what it read): it is read again - at once, or once an answer still arriving is in
   *   (the stored chat then holds the turn too).
   */
  const reloadAfterTurn = useRef(false);
  useEffect(() => {
    if (sending || !reloadAfterTurn.current) return;
    reloadAfterTurn.current = false;
    setLoadTries((n) => n + 1);
  }, [sending]);
  useEffect(() => {
    if (!morning || !morningChatId || loadedFor.current === morningChatId) return;
    let cancelled = false;
    const turnsAtStart = turnsBegun.current;
    (async () => {
      let loaded: Msg[];
      try {
        const res = await api.assistantChatMessages(morningChatId);
        loaded = (res.messages || []).map((m: { id?: string; role: string; content: string; attachments?: AttachmentMeta[] }) => ({
          id: m.id,
          role: m.role === 'assistant' ? 'assistant' : 'user',
          content: m.content ?? '',
          attachments: m.attachments ?? undefined,
        }));
      } catch {
        if (!cancelled) setLoadProblem(CHAT_UNREAD);
        return;
      }
      let cards: ListedSendCard[] = [];
      let cardsUnread = false;
      try {
        cards = (await api.assistantSendCards(morningChatId)).cards ?? [];
      } catch {
        cardsUnread = true;
      }
      if (cancelled) return;
      if (sendingRef.current) { reloadAfterTurn.current = true; return; }
      if (turnsBegun.current !== turnsAtStart) { setLoadTries((n) => n + 1); return; }
      setMessages(cards.length ? [...loaded, { role: 'assistant', content: '', cards }] : loaded);
      setChatId(morningChatId);
      setLoadProblem(cardsUnread ? CARDS_UNREAD : null);
      if (!cardsUnread) loadedFor.current = morningChatId;
    })();
    return () => { cancelled = true; };
  }, [morning, morningChatId, loadTries]);

  /**
   * UI3 - load an existing general chat into this panel.
   *
   * ⭐⭐ IT BUMPS `generationRef` FOR THE SAME REASON `newChat` DOES. A send() in flight captured
   * the old value; without the bump its late stream events would write the previous chat's text
   * into the messages of the one just opened, and re-point `chatId` at the abandoned chat.
   * ⛔ `setMessages` LAST, after the await: setting it first would blank the panel and leave it
   * blank if the fetch fails, which reads as "this chat is empty" rather than "it did not load".
   * ⚠️ The endpoint returns `id, role, content, attachments, created_at` and NOT `tool_calls`, so
   * a reopened chat shows its text without the tool chips it had while streaming. Measured: every
   * stored role is exactly 'user' or 'assistant', which is what `Msg` accepts.
   */
  useEffect(() => {
    if (!openChatId) return;
    // VOICE1: an earlier chat is SHOWN, never recited - and an answer still being read does not carry on over it.
    readAloudRef.current.stop();
    dictationRef.current.cancel();
    voiceChatRef.current?.turnOff();
    let cancelled = false;
    const mySeq = (generationRef.current += 1);
    (async () => {
      try {
        const res = await api.aiConversationMessages('app', openChatId);
        if (cancelled || mySeq !== generationRef.current) return;
        setMessages(
          (res.messages || []).map((m: { id?: string; role: string; content: string; attachments?: AttachmentMeta[] }) => ({
            id: m.id,
            role: m.role === 'assistant' ? 'assistant' : 'user',
            content: m.content ?? '',
            attachments: m.attachments ?? undefined,
          })),
        );
        setChatId(openChatId);
        setViewingEarlierChat(true);
        setPendingFiles([]);
        setAttachError(null);
        /**
         * CHAT-DOCS Task 12: the chat's paperwork, re-signed by the server (a stream card's link lives one hour), shown
         *   at the END of the conversation in creation order - which answer made which is not stored, and a card
         *   pinned to the wrong answer would be a claim. ⛔ Its own try: the messages have already loaded, and a card
         *   that cannot be fetched must never read as "that chat could not be opened".
         */
        try {
          const docs = await api.listChatDocuments(openChatId);
          if (cancelled || mySeq !== generationRef.current || !docs.data?.length) return;
          setMessages((prev) => [...prev, { role: 'assistant', content: '', documents: docs.data }]);
        } catch {
          // The conversation stands without its cards.
        }
      } catch {
        if (!cancelled && mySeq === generationRef.current) {
          setAttachError('That chat could not be opened.');
        }
      } finally {
        if (!cancelled) onChatOpened?.();
      }
    })();
    return () => { cancelled = true; };
  }, [openChatId, onChatOpened]);

  const newChat = async () => {
    generationRef.current += 1;
    readAloud.stop();
    dictation.cancel();
    // ⛔ VOICE1, slice 3: a new chat ENDS the back-and-forth mode - he turns it on again, knowingly.
    voiceChat.turnOff();
    setMessages([]);
    setChatId(null);
    setViewingEarlierChat(false);
    // A file picked for the abandoned chat is not an attachment for the fresh one.
    setPendingFiles([]);
    setAttachError(null);
    try { await api.assistantNewChat({ page_context: renderFactBlock(subject) }); } catch { /* a new chat is created lazily on the next send anyway */ }
  };

  /* VOICE1, size A: the answers read aloud. ⛔ Not drawn where the browser has no voice - never a button that
     does nothing. The control says its own state in words (a control names its contents), and turning it on
     SPEAKS inside this tap: that is what lets the device speak the answers that arrive later (measured).
     CHAT-DUET: a row of the + menu. The menu stays open - the voice picker opens under it. */
  const readAloudRow = readAloud.supported ? (
    <button
      type="button"
      aria-pressed={readAloud.on}
      aria-label={readAloud.on ? 'Read aloud: on' : 'Read aloud: off'}
      onClick={() => (readAloud.on ? readAloud.turnOff() : readAloud.turnOn())}
      className={MENU_ROW}
    >
      <SpeakerIcon className="h-[22px] w-[22px] shrink-0 text-chat-ink-2" />
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-medium leading-[21px] text-chat-ink">
          {readAloud.on ? 'Read aloud: on' : 'Read aloud: off'}
        </span>
        <span className="block text-[13px] leading-[17px] text-chat-ink-2">Says each answer out loud as it arrives</span>
      </span>
      <MenuSwitch on={readAloud.on} />
    </button>
  ) : null;

  /* ⛔ History lives at /ai, which already lists BOTH threads and app chats. A second
     history browser inside a narrow column would be one too many.
     🚨 Link, NOT <a href> - AssistantPanel renders inside Layout inside BrowserRouter,
     so a plain <a> forces a full page reload. On a phone that is a white flash and a
     multi-second reload that reads as "nothing happened". */
  const historyLink = (
    <Link
      to="/ai"
      aria-label="History"
      onClick={(e) => {
        // ⭐ The 112th's review (I2): beside a letter still being written, ask before leaving it - the page, and the
        //   letter with it, unmounts. Then the overlaying rail closes, called with nothing but itself (the 98th).
        if (!mayLeaveTheLetter(rail)) { e.preventDefault(); return; }
        setMenuOpen(false);
        onNavigateAway?.();
      }}
      className={MENU_ROW}
    >
      <ClockIcon className="h-[22px] w-[22px] shrink-0 text-chat-ink-2" />
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-medium leading-[21px] text-chat-ink">History</span>
        <span className="block text-[13px] leading-[17px] text-chat-ink-2">Your earlier chats</span>
      </span>
    </Link>
  );

  /* VOICE-PICK1 (the 134th): the voice the answers are read in. His phone: "how do i get it to sund less robotic?"
     A picker over the English voices the device offers a page (measured on an iPhone: 6 ordinary, 19 novelty),
     remembered on this device. ⛔ CHOOSING SAYS NOTHING - measured: a native menu is not a tap, and speech asked
     for in its change never began - so hearing a voice is a button of its own, said inside ITS tap.
     ⛔ "Hear it" is not drawn in Voice chat or while the microphone is open: a sample is never said into a
     conversation or into the phone's own ear. Not drawn at all where there is nothing to pick. */
  const voicePicker = readAloud.on && readAloud.voices.length > 0 ? (
    <div className="flex min-h-[44px] items-center gap-2 pb-2 pl-[46px] pr-3">
      {/* inline-flex + items-center: on a touch screen the app's own rule makes a label 44px tall (index.css), and
          its word would hang at the top of that box - found by LOOKING at it on the simulator, the 134th. */}
      <label htmlFor="assistant-voice" className="inline-flex shrink-0 items-center text-[13px] text-chat-ink-2">Voice</label>
      {/* text-base below sm for the same reason as the message box: under 16px an iPhone zooms the page on focus. */}
      <select
        id="assistant-voice"
        value={readAloud.voice}
        onChange={(e) => readAloud.chooseVoice(e.target.value)}
        className="min-w-0 flex-1 rounded-lg border-gray-300 py-1.5 text-base sm:text-sm focus:border-primary-500 focus:ring-primary-500"
      >
        <option value="">The phone's own voice</option>
        {readAloud.voices.filter((v) => !v.novelty).map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
        {readAloud.voices.some((v) => v.novelty) && (
          <optgroup label="Novelty and older voices">
            {readAloud.voices.filter((v) => v.novelty).map((v) => <option key={v.id} value={v.id}>{v.label}</option>)}
          </optgroup>
        )}
      </select>
      {!voiceChat.on && dictation.state === 'idle' && (
        <button
          type="button"
          onClick={readAloud.sample}
          className="min-h-[44px] shrink-0 px-2 text-[13px] font-medium text-chat-ink underline-offset-2 hover:underline"
        >
          Hear it
        </button>
      )}
    </div>
  ) : null;

  /* VOICE1, slice 3: the back-and-forth mode. ⛔ Not drawn unless the browser can both listen and speak. ⛔ THE
     CONTROL SAYS ON ITSELF, on and off, that in this mode his words are sent without his reading them - a
     misheard price goes out as heard. The tap itself says one silent word and asks the phone to listen: both
     must start inside a tap (measured). Never remembered - it is off every time the panel opens. */
  /* CHAT-DUET: a row of the + menu. Turning it ON closes the menu in the same tap (the silent word and the listen both
     start inside this tap, as before), so the next thing he sees is it listening. */
  const toggleVoiceChat = () => {
    if (voiceChat.on) { voiceChat.turnOff(); return; }
    voiceChat.turnOn();
    setMenuOpen(false);
  };
  const voiceChatRow = voiceChat.supported ? (
    <button
      type="button"
      aria-pressed={voiceChat.on}
      disabled={sending && !voiceChat.on}
      onClick={toggleVoiceChat}
      className={MENU_ROW}
    >
      <WaveIcon className="h-[22px] w-[22px] shrink-0 text-chat-ink-2" />
      <span className="min-w-0 flex-1">
        <span className="block text-[16px] font-medium leading-[21px] text-chat-ink">
          {voiceChat.on ? 'Voice chat: on' : 'Voice chat: off'}
        </span>
        <span className="block text-[13px] leading-[17px] text-chat-ink-2">{SENT_UNREAD}</span>
      </span>
      <MenuSwitch on={voiceChat.on} />
    </button>
  ) : null;

  /* ⛔ CHAT-DUET: a mode that is on is never hidden in the + menu. While the menu is closed (and he is not typing), this
     line above the box says it in words - Voice chat with its warning ON it, as VOICE1 rules - and a tap turns it off.
     Each control exists ONCE on the screen: the line is not drawn while the menu, which holds the same controls, is open.
     While he types, Voice chat stays (it is what he is using); `typingInMorningBox` is false whenever it is on. */
  const modesOnLine = !menuOpen && !typingInMorningBox && (readAloud.on || voiceChat.on) ? (
    <div className="flex flex-wrap items-center gap-1.5">
      {readAloud.on && (
        <button
          type="button"
          aria-pressed
          aria-label="Read aloud: on"
          onClick={() => readAloud.turnOff()}
          className="inline-flex min-h-[36px] max-w-full items-center gap-1.5 rounded-full bg-white px-3 py-1 text-[13px] font-medium text-chat-ink shadow-chat-float"
        >
          <SpeakerIcon className="h-4 w-4 shrink-0" />
          Read aloud: on
          {/* Spec 4.2: the tap's word, not a symbol ("Read aloud: on. Off"). The name stays the toggle's own. */}
          <span aria-hidden="true" className="font-semibold text-primary-600">Off</span>
        </button>
      )}
      {voiceChat.on && (
        <button
          type="button"
          aria-pressed
          onClick={() => voiceChat.turnOff()}
          className="inline-flex min-h-[36px] max-w-full items-start gap-1.5 rounded-[18px] bg-primary-50 px-3 py-1.5 text-left text-[13px] text-chat-ink shadow-chat-float"
        >
          <WaveIcon className="mt-0.5 h-4 w-4 shrink-0" />
          <span className="min-w-0">
            <span className="block font-semibold">Voice chat: on</span>
            <span className="block text-[12px] leading-snug text-chat-ink-2">{SENT_UNREAD}</span>
          </span>
          {/* Spec 4.2: "Voice chat: on - ... End" - the tap's word, not a symbol. */}
          <span aria-hidden="true" className="shrink-0 self-center font-semibold text-primary-600">End</span>
        </button>
      )}
    </div>
  ) : null;

  /* ⛔ A device that will not speak does not say so (measured: no start, no end, no error) - so the panel does. */
  const silentNote = readAloud.on && readAloud.silent ? (
    <p role="status" className="rounded-[14px] bg-warning-wash px-3 py-1.5 text-[12px] text-warning">
      No sound started. Check the volume, then tap Read aloud off and on again.
    </p>
  ) : null;

  const messageList = messages.map((m, i) => (
    <div key={m.id ?? i} className={m.role === 'user' ? 'text-right' : ''}>
      {m.tools?.length ? (
        <div className="mb-1 flex flex-wrap gap-1">
          {m.tools.map((t, j) => (
            <span key={j} className="rounded-full bg-gray-100 px-2 py-0.5 text-[11px] text-gray-600">
              {t.label ?? t.name}
            </span>
          ))}
        </div>
      ) : null}
      {/* ⛔ `whitespace-pre-wrap` moved onto the USER branch only. It is what made an
          operator's own newlines survive, and it must stay there - but on an assistant
          answer it fights the markdown renderer, which owns its own block spacing.
          max-w-full so a wide table's scroll wrapper cannot stretch this inline-block
          bubble past the 380px rail. */}
      {/* A reopened chat's cards ride on a message of their own with no text: no empty bubble for it. */}
      {/* CHAT-DUET (his pick, 2026-10-28): the Duet bubbles - his in a soft tint of the Apex red, the assistant's light
          grey, 20px corners, 17px on a phone and 15px from sm (bubbles.ts). max-w-[85%] still bounds a wide table's
          scroll wrapper inside the bubble; text-left keeps his own lines reading left inside a right-hand bubble. */}
      {m.content || m.streaming || (!m.documents?.length && !m.cards?.length) ? (
      <div
        data-bubble={m.role === 'user' ? 'user' : 'assistant'}
        className={`inline-block max-w-[85%] text-left ${
          m.role === 'user' ? `whitespace-pre-wrap ${USER_BUBBLE}` : ASSISTANT_BUBBLE
        }`}
      >
        {m.role === 'assistant' ? <ChatMarkdown text={m.content} /> : m.content}
        {m.streaming ? <span className="animate-pulse">|</span> : null}
      </div>
      ) : null}
      {/* ⭐ Amber, not grey. This says a thing the answer claimed did NOT happen, so it has
          to read as a correction to the sentence above it rather than as a footnote. */}
      {m.notes?.length ? (
        <div className="mt-1 space-y-1">
          {m.notes.map((n, j) => (
            <p key={j} className="rounded border border-amber-200 bg-amber-50 px-2 py-1 text-[11px] text-amber-900">
              {n}
            </p>
          ))}
        </div>
      ) : null}
      {/* CHAT-DOCS Task 12: the paperwork this answer made. "File it" goes back through the chat as `file it`
          (a tap, so it is sent like a spoken turn: whatever he was typing stays in the box). */}
      {m.documents?.length ? (
        <div className="space-y-1">
          {m.documents.map((d) => (
            <ChatDocumentCard key={d.id} card={d} disabled={sending} onFile={(words) => { void send(words); }} />
          ))}
        </div>
      ) : null}
      {/* MORNING2: the email cards this answer showed. ⛔ Off while an answer arrives - the chat may be rewriting the draft. */}
      {m.cards?.length ? (
        <div className="mt-1 min-w-0 space-y-2">
          {m.cards.map((c) => <MorningSendCard key={c.id} card={c} disabled={sending} onChange={cardChanged} onSent={cardSent} />)}
        </div>
      ) : null}
      {m.attachments?.length ? (
        <div className={`mt-1 flex flex-wrap gap-1 ${m.role === 'user' ? 'justify-end' : ''}`}>
          {m.attachments.map((a, j) => (
            <span
              key={j}
              className="inline-flex max-w-full items-center gap-1 rounded bg-gray-100 px-1.5 py-0.5 text-[10px] text-gray-600"
              title={`${a.file_name} (${formatFileSize(a.file_size)})`}
            >
              <span className="truncate max-w-[10rem]">{a.file_name}</span>
            </span>
          ))}
        </div>
      ) : null}
    </div>
  ));

  const composer = (
    <>
      {attachError ? (
        <p className="text-[11px] leading-snug text-red-600">{attachError}</p>
      ) : null}

      {/* VOICE1: what the phone is doing with the microphone, in words - and ⛔ why it is not listening, when it
          declines. Measured: a refusal is an error with no end after it, or an end with no error - the hook closes
          on either, so "Waiting" never stays up beside the reason (it did, the 131st's look). */}
      {dictation.state !== 'idle' ? (
        <p role="status" className="text-[11.5px] leading-snug text-gray-600">
          {dictation.state === 'waiting'
            ? 'Waiting for the microphone...'
            : dictation.hearing ? `Hearing: ${dictation.hearing}` : 'Listening...'}
        </p>
      ) : null}
      {/* VOICE1, slice 3: the mode says what it is doing whenever the microphone is not open - why it is waiting
          for his tap, or that an answer is arriving or being read and a tap cuts in (VOICE-CUT1: at any moment).
          ⛔ Never on with nothing on the screen. */}
      {voiceChat.on && dictation.state === 'idle' && held === null && (voiceChat.paused || sending || readAloud.speaking) ? (
        <p role="status" className="text-[11.5px] leading-snug text-gray-600">
          {voiceChat.paused ?? (sending ? ARRIVING : READING_ALOUD)}
        </p>
      ) : null}
      {/* VOICE-CUT1: what he said while the answer he cut into was still arriving - on the screen until it is sent. */}
      {held !== null ? (
        <p role="status" className="text-[11.5px] leading-snug text-gray-600">
          Heard: "{held}". {SENT_WHEN_LANDED}
        </p>
      ) : null}
      {dictation.problem ? (
        <p role="alert" className="text-[11px] leading-snug text-red-600">{dictation.problem}</p>
      ) : null}
      {silentNote}
      {modesOnLine}

      {/* CHAT-DUET: the + menu opens just above the box (this wrapper is its `relative` anchor). */}
      <div className="relative">
        <PlusMenu open={menuOpen} onClose={closeMenu}>
          <button
            type="button"
            aria-label="Attach files"
            title={`Attach files (up to ${MAX_FILES}, 25 MB each)`}
            disabled={sending || pendingFiles.length >= MAX_FILES}
            onClick={() => { setMenuOpen(false); fileInputRef.current?.click(); }}
            className={MENU_ROW}
          >
            <ClipIcon className="h-[22px] w-[22px] shrink-0 text-chat-ink-2" />
            <span className="min-w-0 flex-1">
              <span className="block text-[16px] font-medium leading-[21px] text-chat-ink">Attach files</span>
              <span className="block text-[13px] leading-[17px] text-chat-ink-2">PDFs, photos, spreadsheets - up to 25 MB each</span>
            </span>
          </button>
          {readAloudRow}
          {voicePicker}
          {voiceChatRow}
          {historyLink}
        </PlusMenu>
        <input
          ref={fileInputRef}
          type="file"
          multiple
          className="hidden"
          accept=".pdf,.jpg,.jpeg,.png,.webp,.xlsx,.xls,.docx,.doc,.csv,.txt"
          onChange={handleFileSelect}
        />
        <ChatBox
          top={pendingFiles.length ? pendingFiles.map((pf, i) => (
            <span
              key={i}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-chat-bubble px-2 py-0.5 text-[11px] text-chat-ink"
            >
              {pf.uploading ? <span className="animate-pulse text-primary-500">...</span> : null}
              <span className="truncate max-w-[9rem]">{pf.file.name}</span>
              <span className="flex-shrink-0 text-chat-ink-2">{formatFileSize(pf.file.size)}</span>
              <button
                type="button"
                aria-label={`Remove ${pf.file.name}`}
                onClick={() => removePendingFile(i)}
                disabled={sending}
                className="flex-shrink-0 px-0.5 text-chat-ink-2 hover:text-chat-ink disabled:opacity-50"
              >
                x
              </button>
            </span>
          )) : null}
        >
          <button
            type="button"
            aria-label="More options"
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((o) => !o)}
            className={ICON_BUTTON}
          >
            <PlusIcon className="h-[22px] w-[22px]" />
          </button>
          <input
            ref={inputRef}
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); void send(); } }}
            placeholder={subject && !morning ? `Ask about ${subject.label.slice(0, 24)}...` : 'Ask anything...'}
            /*
             * 🚨 text-base BELOW sm, NOT text-sm. iOS Safari AUTO-ZOOMS the whole page whenever it
             * focuses an input whose font-size is under 16px, and it does not zoom back out. On
             * his phone that clipped the tab strip, the History link and the Send button off both
             * edges the moment he tapped the box - it looked like a layout bug and was a font size.
             * 16px below 640px kills the zoom; the docked desktop rail keeps 14px via sm:text-sm.
             * CHAT-DUET: CHAT_INPUT keeps exactly that (text-base sm:text-sm), borderless inside the round box.
             */
            className={CHAT_INPUT}
          />
          {/* VOICE1: the microphone. ⛔ Not drawn where the browser cannot listen. The tap itself asks the phone to
              listen (it will not start outside one), and the voice is stopped first - the phone must not hear the
              app talking. A second tap finishes; the words land in the box and wait for Send. */}
          {dictation.supported && (
            <button
              type="button"
              aria-label={dictation.state !== 'idle' ? 'Stop listening' : voiceChat.on ? 'Talk' : 'Dictate'}
              aria-pressed={dictation.state !== 'idle'}
              title={dictation.state !== 'idle' ? 'Stop listening' : voiceChat.on ? 'Talk' : 'Dictate'}
              onClick={() => {
                // VOICE1, slice 3: in the back-and-forth mode the tap is the mode's - it cuts the voice off and
                // listens, or finishes a listen under way; what is heard is then SENT.
                // ⛔ VOICE-CUT1: while an answer is still ARRIVING the tap also marks this turn as cut into - the
                //   rest of that answer is not read. ⛔ Only then: a tap between turns is an ordinary turn.
                if (voiceChat.on) {
                  if (sending) cutIn.current = true;
                  voiceChat.micTapped();
                  return;
                }
                if (dictation.state !== 'idle') { dictation.stop(); return; }
                readAloud.stop();
                dictation.start();
              }}
              disabled={sending && !voiceChat.on}
              className={`${ICON_BUTTON} ${dictation.state !== 'idle' ? 'bg-primary-500 text-white hover:bg-primary-600' : ''}`}
            >
              <MicIcon className="h-[21px] w-[21px]" />
            </button>
          )}
          <SendButton disabled={sending || !draft.trim()} onClick={() => void send()} />
        </ChatBox>
      </div>
    </>
  );

  if (!morning) return (
    <div className="flex flex-col h-full min-h-0">
      <div className="flex items-center justify-end gap-2 px-3 py-2 border-b border-gray-200">
        {/* CHAT-DUET: Read aloud and History moved into the chat box's + menu; New chat stays here, alone. */}
        {/* ⛔ UI2 MOVED THE SUBJECT LINE TO THE RAIL HEADER, and deleted the copy that stood here
            (`About: {label}` / `General`). Not a tidy-up: the rail's header sits directly above
            this row and serves BOTH tabs, so keeping this one said the same thing twice in a
            342px column - and two places rendering one fact is the defect class this build is
            made of. Both read the same `usePageSubject()`, so nothing can now disagree.
            ⭐ `subject` is still read by this component: `renderFactBlock(subject)` is what the
            model is told, which is a different job from telling the operator. */}
        <div className="flex items-center gap-2 shrink-0">
          {/* min-h-[44px] + inline-flex items-center: Apple's minimum tap target is 44 CSS px.
              The text stays text-xs - only the hit area grows, via padding, not font size. */}
          <button
            onClick={newChat}
            disabled={sending}
            className="min-h-[44px] px-2 inline-flex items-center text-xs text-primary-500 hover:underline disabled:opacity-50"
          >
            New chat
          </button>
        </div>
      </div>

      {/*
        ⛔⛔ THE PANEL SAYS WHEN IT IS SHOWING HISTORY. An earlier chat may be about a DIFFERENT
        record from the one on screen - measured live, an old chat about a 12,036 SF building read
        while standing on a 5,000 SF one - and a follow-up typed here continues THAT chat while
        carrying THIS page's fact block. Both halves are defensible; what is not defensible is
        letting them mix silently. It names the state and offers the way out.
      */}
      {viewingEarlierChat && (
        <div className="flex items-center gap-2 border-b border-warning/30 bg-warning-wash px-3 py-1.5 text-[11.5px] text-warning">
          <span className="min-w-0 flex-1">
            Showing an earlier chat. A reply continues it
            {subject ? `, with ${subject.label} as the record on screen` : ''}.
          </span>
          <button
            type="button"
            onClick={newChat}
            className="shrink-0 font-semibold underline-offset-[3px] hover:underline"
          >
            New chat
          </button>
        </div>
      )}

      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-3 min-h-0">
        {/* Full-screen on a phone, an empty message list reads as a blank, broken screen rather
            than an unstarted chat - unlike the 380px desktop column where it merely reads as
            empty. Say what this tab IS: private, unlike the Thread tab. Same muted-text
            treatment as ThreadPanel's empty state. */}
        {messages.length === 0 ? (
          <p className="text-sm text-gray-500">
            No messages yet. This chat is private to you - the Thread tab is what Robert sees.
          </p>
        ) : null}
        {messageList}
        <div ref={bottomRef} />
      </div>

      {/* ⚠️ The rail is 380px docked. Everything here wraps and truncates - a long file name must
          never widen the composer. */}
      <div className="border-t border-gray-200 p-2 space-y-1.5">
        {composer}
      </div>
    </div>
  );

  /**
   * MORNING2 (Task 9) - the chat on the Morning page: ONE page scroll, his Duet pick (2026-10-28). The brief (`lead`)
   * first, then the conversation, then the chat box pinned at the bottom (`sticky bottom-0`, above the phone's home bar)
   * with the voice controls - Read aloud, the voice, Voice chat - and today's +, microphone and Send. No New chat (one
   * chat a day) and no "earlier chat" banner (it is always today's).
   */
  return (
    <div ref={morningPanelRef} className="relative flex min-w-0 flex-col">
      <div className="min-w-0 space-y-3 pb-3">
        {lead}
        {loadProblem ? (
          <div role="alert" className="flex flex-wrap items-center gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
            <span className="min-w-0 break-words">{loadProblem}</span>
            <button
              type="button"
              disabled={sending}
              onClick={() => setLoadTries((n) => n + 1)}
              className="rounded-full border border-red-300 bg-white px-3 py-1 text-[13px] font-medium text-red-700 hover:border-red-500 disabled:opacity-50"
            >
              Try again
            </button>
          </div>
        ) : null}
        {messageList}
      </div>
      {/* ⚠️ Phone first: -mx-4 only cancels the page's own 16px gutter, so the bar meets both edges and nothing scrolls
          sideways; from sm up it sits in the column. */}
      {/* CHAT-DUET: the round box floats on the page's own colour (no white bar, no border), and sits right above the
          phone bar while that is drawn (`--tab-bar-space`, set in index.css; 0 everywhere else, and while he types).
          While he types it sits ON the keyboard: the keyboard covers the home indicator's edge, so the safe-area space
          under the box goes (found by looking on the iPhone simulator - it floated about 50 points above the keys). */}
      <div
        ref={morningBarRef}
        data-sits-on-keyboard=""
        className={`sticky bottom-[var(--tab-bar-space,0px)] z-10 -mx-4 space-y-1.5 bg-[linear-gradient(to_bottom,transparent,#F4F4F5_44px)] px-4 pt-3 ${keyboard.up ? 'pb-2' : 'pb-[var(--dock-pad,calc(0.5rem+env(safe-area-inset-bottom)))]'} sm:mx-0 sm:px-2`}
      >
        {composer}
      </div>
      {/* While he types: white under the box, where the keyboard's see-through ✓ bar would show the list (useKeyboardUp).
          Page content, not part of the pinned box - the box's own layer is clipped there. */}
      {keyboard.strip && (
        <div
          aria-hidden="true"
          data-testid="morning-keyboard-strip"
          className="pointer-events-none absolute -left-4 -right-4 z-[9] bg-[#F4F4F5] sm:left-0 sm:right-0"
          style={{ top: keyboard.strip.top, height: keyboard.strip.height }}
        />
      )}
      {/* ⛔ The answer's end is followed to HERE, after the pinned chat box: scrolled to its own place the box sits right
          under the newest line, so an answer arriving is never under the box (its height changes with the voice rows). */}
      <div ref={bottomRef} />
    </div>
  );
}
